import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@mui/material';
import { getStripeSyncLogs } from '../api/stripeApi';
import { colorTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';

const { semantic } = colorTokens;

const monoCellSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  maxWidth: 220,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const messageCellSx = {
  ...monoCellSx,
  maxWidth: 280,
  color: semantic.text.secondary,
};

function syncStatusTone(status) {
  if (status === 'success' || status === 'succeeded' || status === 'synced') return 'Success';
  if (status === 'failed' || status === 'error') return 'Danger';
  if (status === 'skipped' || status === 'ignored') return 'Neutral';
  if (status === 'pending' || status === 'queued') return 'Info';
  return 'Neutral';
}

function syncStatusLabel(status, t) {
  if (!status) return null;
  const key = `stripe:sync_logs.statuses.${status}`;
  return t(key, { defaultValue: status });
}

function TechText({ value, emptyValue, sx }) {
  if (!value && value !== 0) return emptyValue;
  const text = String(value);
  return (
    <Typography component="span" title={text} sx={sx || monoCellSx}>
      {text}
    </Typography>
  );
}

export function StripeSyncLogsPage() {
  const { t } = useTranslation(['stripe', 'common']);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const emptyValue = t('common:empty_value');
  const recordCount = logs.length;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { per_page: 100 };
      if (statusFilter) params.status = statusFilter;
      const response = await getStripeSyncLogs(params);
      setLogs(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const columns = useMemo(
    () => [
      {
        id: 'entity_type',
        label: t('stripe:sync_logs.columns.entity'),
        render: (row) => (
          <TechText value={row.entity_type} emptyValue={emptyValue} />
        ),
      },
      {
        id: 'entity_id',
        label: t('stripe:sync_logs.columns.entity_id'),
        render: (row) => (
          <TechText value={row.entity_id} emptyValue={emptyValue} />
        ),
      },
      {
        id: 'stripe_object_id',
        label: t('stripe:sync_logs.columns.stripe_object'),
        render: (row) => (
          <TechText value={row.stripe_object_id} emptyValue={emptyValue} />
        ),
      },
      {
        id: 'action',
        label: t('stripe:sync_logs.columns.action'),
        render: (row) => (
          <TechText value={row.action} emptyValue={emptyValue} />
        ),
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? (
              <StatusBadge
                status={row.status}
                category="generic"
                apiLabel={syncStatusLabel(row.status, t)}
                size="Sm"
                tone={syncStatusTone(row.status)}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'message',
        label: t('stripe:sync_logs.columns.message'),
        render: (row) => (
          <TechText value={row.message} emptyValue={emptyValue} sx={messageCellSx} />
        ),
      },
      {
        id: 'created_at',
        label: t('common:created'),
        render: (row) => row.created_at || emptyValue,
      },
    ],
    [emptyValue, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('stripe:sync_logs.title')}
        subtitle={t('stripe:sync_logs.subtitle')}
        actions={(
          <Button variant="Secondary" component={RouterLink} to="/stripe">
            {t('stripe:dashboard.title')}
          </Button>
        )}
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadLogs} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('common:load_failed')}
        </Alert>
      )}

      <FilterBar type="Simple">
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          <FilterSelectOption value="success">
            {t('stripe:sync_logs.statuses.success')}
          </FilterSelectOption>
          <FilterSelectOption value="failed">
            {t('stripe:sync_logs.statuses.failed')}
          </FilterSelectOption>
          <FilterSelectOption value="skipped">
            {t('stripe:sync_logs.statuses.skipped')}
          </FilterSelectOption>
        </FilterSelect>
      </FilterBar>

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('stripe:sync_logs.logs')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('stripe:sync_logs.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={logs}
            getRowKey={(row) => row.id}
            emptyMessage={t('common:no_data')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>
    </Box>
  );
}
